import { Fragment, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { PaginationBar } from "@/components/ui/PaginationBar";
import { ChevronDown, ChevronRight, Search } from "lucide-react";
import type { Order, OrderItem, OrderItemSowingInstruction } from "../../services/ordersApi";

interface OrdersReadyForSowingTableProps {
  orders: Order[];
  onSelectOrderItem?: (order: Order, itemId: string) => void;
  isLoading?: boolean;
  isFetching?: boolean;
  searchTerm?: string;
  onSearchTermChange?: (value: string) => void;
  currentPage?: number;
  pageSize?: number;
  totalCount?: number;
  totalPages?: number;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
}

const SI_STATUS_CONFIG: Record<string, { label: string; className: string }> = {
  Pending:          { label: "Pending",           className: "bg-yellow-100 text-yellow-800 border-yellow-200" },
  Issued:           { label: "Issued",             className: "bg-blue-100 text-blue-800 border-blue-200" },
  Sown:             { label: "Sown",               className: "bg-indigo-100 text-indigo-800 border-indigo-200" },
  Germinated:       { label: "Germinated",         className: "bg-cyan-100 text-cyan-800 border-cyan-200" },
  Growing:          { label: "Growing",            className: "bg-green-100 text-green-800 border-green-200" },
  ReadyForDispatch: { label: "Ready for Dispatch", className: "bg-teal-100 text-teal-800 border-teal-200" },
  Stored:           { label: "Stored",             className: "bg-slate-100 text-slate-700 border-slate-200" },
  Dispatched:       { label: "Dispatched",         className: "bg-purple-100 text-purple-800 border-purple-200" },
  Overgrown:        { label: "Overgrown",          className: "bg-orange-100 text-orange-800 border-orange-200" },
  Revoked:          { label: "Revoked",            className: "bg-red-100 text-red-800 border-red-200" },
  OnHold:           { label: "On Hold",            className: "bg-gray-100 text-gray-700 border-gray-200" },
};

function SowingInstructionRow({ si }: { si: OrderItemSowingInstruction }) {
  const cfg = SI_STATUS_CONFIG[si.status] ?? { label: si.status, className: "bg-gray-100 text-gray-700 border-gray-200" };
  return (
    <TableRow className="bg-muted/20 hover:bg-muted/30">
      <TableCell className="px-2" />
      <TableCell className="px-2" />
      <TableCell className="pl-12" colSpan={2}>
        <span className="text-xs font-mono text-muted-foreground">{si.instructionNumber}</span>
        {si.greenhouseName && (
          <span className="text-xs text-muted-foreground ml-2">· Chamber: {si.greenhouseName}</span>
        )}
      </TableCell>
      <TableCell>
        <Badge variant="outline" className={`text-xs ${cfg.className}`}>{cfg.label}</Badge>
      </TableCell>
      <TableCell className="text-xs text-muted-foreground">
        {si.scheduledSowingDate
          ? new Date(si.scheduledSowingDate).toLocaleDateString()
          : "—"}
      </TableCell>
      <TableCell className="text-xs text-muted-foreground">
        {si.quantityToSow} sowing
      </TableCell>
      <TableCell />
    </TableRow>
  );
}

// FulfillmentSource enum values
const FULFILLMENT_SOURCE = {
  Unknown: 0,
  FromStock: 1,
  PartialStock: 2,
  NeedsNewSowing: 3,
  CustomerSeeds: 4,
} as const;

/** Returns true if this item still needs a sowing instruction to be raised */
function itemNeedsSowing(item: OrderItem): boolean {
  // Customer seeds always need sowing
  if (!item.isCompanySeedling) return true;

  // Explicitly needs new sowing
  if (item.fulfillmentSource === FULFILLMENT_SOURCE.NeedsNewSowing) return true;

  // Partial stock — part reserved, remainder needs sowing
  if (item.fulfillmentSource === FULFILLMENT_SOURCE.PartialStock) return true;

  // FromStock items are fulfilled via reservations against existing pipeline —
  // they never need new sowing.
  if (item.fulfillmentSource === FULFILLMENT_SOURCE.FromStock) return false;

  // Unknown/legacy — show to be safe
  return true;
}

/** Returns true if the item still has unallocated quantity that needs a new sowing instruction */
function itemCanCreateMoreSowing(item: OrderItem): boolean {
  const totalAllocated = item.sowingInstructions.reduce((sum, si) => sum + si.quantityToSow, 0);

  if (item.fulfillmentSource === FULFILLMENT_SOURCE.PartialStock) {
    const reserved = item.availableStockAtOrderTime ?? 0;
    return totalAllocated < item.quantity - reserved;
  }

  return totalAllocated < item.quantity;
}

/** Calculates how many trays are already covered (reserved from stock or allocated to sowing instructions) */
function getStockCoverage(item: OrderItem): { reserved: number; needsSowing: number } | null {
  if (item.fulfillmentSource === FULFILLMENT_SOURCE.PartialStock) {
    const reserved = item.availableStockAtOrderTime ?? 0;
    return { reserved, needsSowing: item.quantity - reserved };
  }
  if (item.fulfillmentSource === FULFILLMENT_SOURCE.FromStock) {
    // Fully covered by reservations — no sowing needed
    return null;
  }
  return null;
}

function OrderItemRow({
  item,
  isExpanded,
  onToggle,
  onCreateSowing,
}: {
  item: OrderItem;
  isExpanded: boolean;
  onToggle: () => void;
  onCreateSowing?: () => void;
}) {
  const hasSI = item.hasSowingInstructions && item.sowingInstructions.length > 0;
  const coverage = getStockCoverage(item);

  return (
    <>
      <TableRow
        className="bg-muted/30 hover:bg-muted/40 cursor-pointer border-l-2 border-l-border"
        onClick={onToggle}
      >
        {/* indent + chevron */}
        <TableCell className="px-2" />
        <TableCell className="px-2">
          {item.sowingInstructions.length > 0 ? (
            isExpanded
              ? <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
              : <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
          ) : (
            <span className="w-3.5 inline-block" />
          )}
        </TableCell>
        <TableCell className="pl-4" colSpan={2}>
          <div className="flex flex-col gap-0.5">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">{item.productName}</span>
              {item.trayTypeName && (
                <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                  {item.trayTypeName}
                </Badge>
              )}
            </div>
            {(item.cropType || item.variety) && (
              <span className="text-xs text-muted-foreground">
                {[item.cropType, item.variety].filter(Boolean).join(" · ")}
              </span>
            )}
            {coverage && (
              <span className="text-xs text-amber-600 font-medium">
                {coverage.reserved} trays reserved from stock · {coverage.needsSowing} trays need sowing
              </span>
            )}
          </div>
        </TableCell>
        <TableCell>
          {hasSI ? (
            <Badge variant="secondary" className="text-xs">
              {item.sowingInstructions.length} instruction{item.sowingInstructions.length !== 1 ? "s" : ""}
            </Badge>
          ) : (
            <Badge variant="outline" className="text-xs border-dashed text-muted-foreground">
              No instruction
            </Badge>
          )}
        </TableCell>
        <TableCell className="text-sm text-muted-foreground">
          {item.quantityPending} / {item.quantity} pending
        </TableCell>
        <TableCell />
        <TableCell className="text-right">
          {onCreateSowing && (
            <Button
              size="sm"
              onClick={(e) => { e.stopPropagation(); onCreateSowing(); }}
            >
              Create Sowing
            </Button>
          )}
        </TableCell>
      </TableRow>

      {/* Level 2: sowing instructions */}
      {isExpanded && item.sowingInstructions.map((si) => (
        <SowingInstructionRow key={si.id} si={si} />
      ))}
    </>
  );
}

export const OrdersReadyForSowingTable = ({
  orders,
  onSelectOrderItem,
  isLoading = false,
  isFetching = false,
  searchTerm = "",
  onSearchTermChange,
  currentPage = 1,
  pageSize = 20,
  totalCount = orders.length,
  totalPages = Math.max(1, Math.ceil(orders.length / pageSize)),
  onPageChange,
  onPageSizeChange,
}: OrdersReadyForSowingTableProps) => {
  const [expandedOrders, setExpandedOrders] = useState<Set<string>>(new Set());
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());

  const toggleOrder = (id: string) =>
    setExpandedOrders((prev) => {
      const s = new Set(prev);
      if (s.has(id)) {
        s.delete(id);
      } else {
        s.add(id);
      }
      return s;
    });

  const toggleItem = (id: string) =>
    setExpandedItems((prev) => {
      const s = new Set(prev);
      if (s.has(id)) {
        s.delete(id);
      } else {
        s.add(id);
      }
      return s;
    });

  if (isLoading) {
    return (
      <div className="rounded-md border">
        <div className="flex items-center justify-center h-40">
          <p className="text-gray-500">Loading orders...</p>
        </div>
      </div>
    );
  }

  if (orders.length === 0) {
    return (
      <div className="space-y-3">
        {onSearchTermChange && (
          <div className="relative max-w-sm">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search orders or customers..."
              value={searchTerm}
              onChange={(event) => onSearchTermChange(event.target.value)}
              className="pl-8"
            />
          </div>
        )}
        <div className="rounded-md border">
          <div className="flex items-center justify-center h-40">
            <p className="text-gray-500">No orders ready for sowing</p>
          </div>
        </div>
        {onPageChange && onPageSizeChange && (
          <PaginationBar
            currentPage={currentPage}
            pageSize={pageSize}
            totalCount={totalCount}
            totalPages={Math.max(1, totalPages)}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
          />
        )}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {onSearchTermChange && (
        <div className="flex items-center gap-3">
          <div className="relative max-w-sm flex-1">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search orders or customers..."
              value={searchTerm}
              onChange={(event) => onSearchTermChange(event.target.value)}
              className="pl-8"
            />
          </div>
          {isFetching && <span className="text-sm text-muted-foreground">Refreshing...</span>}
        </div>
      )}
      <div className="rounded-md border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-8" />
              <TableHead className="w-8" />
              <TableHead>Order / Item</TableHead>
              <TableHead>Customer</TableHead>
              <TableHead>Instructions</TableHead>
              <TableHead>Qty</TableHead>
              <TableHead>Planned Planting</TableHead>
              <TableHead className="text-right">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {orders.map((order) => {
              const isOrderExpanded = expandedOrders.has(order.id);
              // Only show items that actually need sowing action
              const actionableItems = order.orderItems.filter(itemNeedsSowing);
              const itemsWithoutSI = actionableItems.filter((i) => !i.hasSowingInstructions).length;

              if (actionableItems.length === 0) return null;

              return (
                <Fragment key={order.id}>
                  {/* Level 1: Order row */}
                  <TableRow
                    className="cursor-pointer hover:bg-muted/50 font-medium"
                    onClick={() => toggleOrder(order.id)}
                  >
                    <TableCell className="px-2">
                      {isOrderExpanded
                        ? <ChevronDown className="h-4 w-4 text-muted-foreground" />
                        : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
                    </TableCell>
                    <TableCell className="px-2" />
                    <TableCell className="font-medium">{order.orderNumber}</TableCell>
                    <TableCell>{order.customerName}</TableCell>
                    <TableCell>
                      <span className="text-sm text-muted-foreground">
                        {actionableItems.length} item{actionableItems.length !== 1 ? "s" : ""} need sowing
                      </span>
                      {itemsWithoutSI > 0 && (
                        <Badge variant="destructive" className="ml-2 text-xs">
                          {itemsWithoutSI} without instruction
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell />
                    <TableCell>
                      {order.plannedPlantingDate
                        ? new Date(order.plannedPlantingDate).toLocaleDateString()
                        : "—"}
                    </TableCell>
                    <TableCell />
                  </TableRow>

                  {/* Level 2: Only show items that need sowing */}
                  {isOrderExpanded && actionableItems.map((item) => (
                    <OrderItemRow
                      key={item.id}
                      item={item}
                      isExpanded={expandedItems.has(item.id)}
                      onToggle={() => toggleItem(item.id)}
                      onCreateSowing={onSelectOrderItem && itemCanCreateMoreSowing(item) ? () => onSelectOrderItem(order, item.id) : undefined}
                    />
                  ))}
                </Fragment>
              );
            })}
          </TableBody>
        </Table>
      </div>
      {onPageChange && onPageSizeChange && (
        <PaginationBar
          currentPage={currentPage}
          pageSize={pageSize}
          totalCount={totalCount}
          totalPages={Math.max(1, totalPages)}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
        />
      )}
    </div>
  );
};
